import React from 'react';
import { useState } from 'react';
import RequiredFieldDisclaimer from '../components/RequiredFieldDisclaimer';
import SkipStep from '../components/SkipStep';
import AccessionedOrderAlert from '../components/AccessionedOrderAlert';
import {getCustomerCareNumber, isUKOrder} from '../util/gearOrderUtil';

const InsuranceUK = (props) => {

  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [formData, setFormData] = useState({
    provider: { value: props.gearOrder.insurance?.insuranceProvider || '' },
    otherProvider: { value: props.gearOrder.insurance?.otherProvider || '' },
    subscriberId: { value: props.gearOrder.insurance?.subscriberId || '' },
    preAuthCode: { value: props.gearOrder.insurance?.preAuthCode || '' }
  });
  let pageLocked = props.userInfo.adminUser !== 'true' && props.gearOrder && props.gearOrder.requisitions
    && props.gearOrder.requisitions.some(req => req.accessionNo);


  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.currentTarget.dataset.fieldname]: { value: e.currentTarget.value }
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (pageLocked) {
      props.updateStep(props.gearOrder, 'insurance', 'Next');
    } else {
      submitInsuranceInfoUK();
    }
  }

  const validateInsuranceForm = (data) => {
    let valid = true;
    let validatedForm = { ...data };
    if (!data.provider.value) {
      valid = false;
      validatedForm.provider = { ...data.provider, valid: false, message: 'Select an insurance provider.' };
    } else {
      validatedForm.provider = { ...data.provider, valid: true, message: '' };
    }
    if (data.provider.value === 'other' && !data.otherProvider.value) {
      valid = false;
      validatedForm.otherProvider = { ...data.otherProvider, valid: false, message: 'Other Insurance Company is required when "My insurance is not listed / All other companies" is selected.' };
    } else {
      validatedForm.otherProvider = { ...data.otherProvider, valid: true, message: '' };
    }
    if (!data.subscriberId.value) {
      valid = false;
      validatedForm.subscriberId = { ...data.subscriberId, valid: false, message: 'Enter your subscriber number.' };
    } else {
      validatedForm.subscriberId = { ...data.subscriberId, valid: true, message: '' };
    }
    if (!data.preAuthCode.value) {
      valid = false;
      validatedForm.preAuthCode = { ...data.preAuthCode, valid: false, message: 'Enter your pre-authorization number.' };
    } else {
      validatedForm.preAuthCode = { ...data.preAuthCode, valid: true, message: '' };
    }

    return { isValid: valid, validatedForm: validatedForm };
  }

  const submitInsuranceInfoUK = React.useCallback(() => {
    let validatedForm = validateInsuranceForm(formData);
    const isValid = validatedForm.isValid;
    setFormData(validatedForm.validatedForm);
    setErrorMessage(isValid ? '' : 'Please correct the errors below and resubmit the form.');
    setShowError(!isValid);
    if (isValid) {
      let order = Object.assign({}, props.gearOrder);
      let insurance = {
        insuranceProvider: formData.provider.value,
        subscriberId: formData.subscriberId.value,
        preAuthCode: formData.preAuthCode.value
      };
      if (formData.provider.value === 'other') {
        insurance.otherProvider = formData.otherProvider.value;
      }
      order.patientInfo = order.patientInfo || {};
      order['insurance'] = insurance;

      return fetch(process.env.REACT_APP_GEAR_REST_URL + '/order/' + order.id + '/insurance/', {
        credentials: 'include',
        mode: 'cors',
        method: 'PUT',
        timeout: 1000,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(order)
      }).then(response => {
        if (response.ok) {
          return response.json();
        } else {
          return Promise.reject(`an unexpected error has occurred. please call us at ${getCustomerCareNumber(props.gearOrder)}`);
        }
      }).then(json => {
        if (json.id) {
          props.setGearOrder(json);
          props.setInsuranceOptions([]);
          props.updateStep(json, 'insurance', 'Next');
          return Promise.resolve(json);
        } else if (json.errors) {
          props.setShowError(true);
          props.setErrorMessage(json.errors[0].field + ' ' + json.errors[0].defaultMessage);
          return Promise.reject(`an unexpected error has occurred. please call us at ${getCustomerCareNumber(props.gearOrder)}`);
        }
      }).catch(error => {
        props.setShowError(true);
        props.setErrorMessage(`The network is currently unavailable. Please call us at ${getCustomerCareNumber(props.gearOrder)}`);
      });

    } else if (props.editRef) {
      return Promise.reject('ValidationError');    
    }
  }, [formData, props]);

  React.useEffect(() => {
    if (props.editRef) {
      props.editRef({
        save: submitInsuranceInfoUK
      });
    }
  }, [props, submitInsuranceInfoUK]);

  return (
    <div>
      <div className="row">
        <div className="col-md-12">
          <h2>Insurance Information</h2>
          {!props.gearOrder.containsCovidRegistration &&
            <SkipStep hidden={pageLocked || props.editRef}
              orderId={props.gearOrder.id}
              stepKey="insurance"
              getWorkflowStep={props.getWorkflowStep}
              updateStep={props.updateStep}
              setGearOrder={props.setGearOrder}
              userType={props.userInfo.type}
              setDirty={props.setDirty} />
          }
          <div>
            {pageLocked && (
              <AccessionedOrderAlert isUKOrder={isUKOrder(props.gearOrder)} />
            )}
            <div id="validationAlert">
              <div className="alert alert-danger alert-dismissible" role="alert" hidden={!showError}>
                <button type="button" className="close" data-bs-dismiss="alert" aria-label="Close">
                  <span aria-hidden="true"></span></button>
                <strong>{errorMessage}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="row grid-space-20">
        <div className="col-md-12 col-lg-12">
          <div className="space-top"></div>
          <div>
            <h4>Genova Diagnostics currently provides insurance billing with a pre-authorization number upon completion of the following information.</h4>
            <div className="separator-2"></div>
          </div>
          <form onSubmit={handleSubmit} autoComplete="off" noValidate>
            <div className="form-group has-feedback">
              <div className="row">
                <div className={'form-group col-md-4 ' + (formData.provider.valid === false ? 'has-error' : '')}>
                  <label className="control-label" htmlFor="insurance-insuranceProvider">Insurance Company<span className="text-danger">*</span></label>
                  <select name="insuranceInfo"
                    id="insurance-insuranceProvider"
                    className="form-select"
                    value={formData.provider.value}
                    data-fieldname="provider"
                    onChange={handleChange}
                    disabled={pageLocked}
                  >
                    <option value=""></option>
                    {<>
                      <optgroup label="Commercial Carriers">
                        <option value="allianz">Allianz</option>
                        <option value="aviva">Aviva</option>
                        <option value="axa-ppp">Axa-PPP</option>
                        <option value="bupa">BUPA</option>
                        <option value="bupa-intl">BUPA international</option>
                        <option value="cigna">Cigna</option>
                        <option value="healix">Healix</option>
                        <option value="wpa">WPA</option>
                      </optgroup>
                      <option value="other">My insurance is not listed / All other companies</option>
                    </>}
                  </select>
                  {formData.provider.valid === false && <span
                    className="validation-message text-danger">{formData.provider.message}</span>}
                </div>
                <div className={'form-group col-md-4 ' + (formData.subscriberId.valid === false ? 'has-error' : '')}>
                  <label className="control-label" htmlFor="insurance-subscriberId">Subscriber Number<span
                    className="text-danger">*</span></label>
                  <input className="form-control"
                    name="insuranceInfo"
                    id="insurance-subscriberId"
                    maxLength="50"
                    type="text"
                    pattern="[a-zA-Z0-9]+"
                    value={formData.subscriberId.value}
                    autoComplete="off"
                    data-fieldname="subscriberId"
                    onChange={handleChange}
                    disabled={pageLocked}>
                  </input>
                  {formData.subscriberId.valid === false && <span
                    className="validation-message text-danger">{formData.subscriberId.message}</span>}
                </div>
                <div className={'form-group col-md-4 ' + (formData.preAuthCode.valid === false ? 'has-error' : '')}>
                  <label className="control-label" htmlFor="insurance-preAuthCode">Pre-Authorization Number<span
                    className="text-danger">*</span></label>
                  <input className="form-control"
                    name="insuranceInfo"
                    id="insurance-preAuthCode"
                    maxLength="50"
                    type="text"
                    pattern="[a-zA-Z0-9]+"
                    value={formData.preAuthCode.value}
                    autoComplete="off"
                    data-fieldname="preAuthCode"
                    onChange={handleChange}
                    disabled={pageLocked}>
                  </input>
                  {formData.preAuthCode.valid === false && <span
                    className="validation-message text-danger">{formData.preAuthCode.message}</span>}
                </div>
              </div>
              <div className="row">
                <div className={'form-group col-md-12 ' + (formData.otherProvider.valid === false ? 'has-error' : '')}
                  hidden={formData.provider.value !== 'other'}>
                  <label className="control-label" htmlFor="insurance-otherProvider">Other Insurance Company<span
                    className="text-danger">*</span></label>
                  <input className="form-control"
                    name="insuranceInfo"
                    id="insurance-otherProvider"
                    maxLength="100"
                    type="text"
                    value={formData.otherProvider.value}
                    data-fieldname="otherProvider"
                    onChange={handleChange}
                    disabled={pageLocked}>
                  </input>
                  {formData.otherProvider.valid === false && <span
                    className="validation-message text-danger">{formData.otherProvider.message}</span>}
                </div>
              </div>
            </div>
            <div className="space"></div>
            <RequiredFieldDisclaimer />
            <div className="alert alert-warning">
              All information must be provided for us to process your insurance claim through your insurance provider.
              Failure to provide this information with the requisition and sample submission will result in an invoice to
              the patient for the full amount of the test.
            </div>
            {!props.editRef && <div className="row sticky-bottom">
              <div className="col-12">
                <button className="btn btn-white rounded-pill btn-sm margin-clear" type="button" onClick={props.goBack}>
                  <i className="pe-2 fa fa-arrow-circle-left"></i>
                  Back
                </button>
                <button className="btn btn-primary rounded-pill btn-sm margin-clear pull-right" type="submit">
                  Next
                  <i className="ps-2 fa fa-arrow-circle-right"></i>
                </button>
              </div>
            </div>}
          </form>
        </div>
      </div>
    </div>
  );
};

export default InsuranceUK;
